import { ChevronRightIcon } from '@heroicons/react/24/solid';
import { Dispatch, SetStateAction, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { Capability, CapabilityParameter } from '@/hooks/useCapabilities';
import { ParameterInput } from '@/sections/attacks/capabilities/components/inputs';

import { OutputSettings } from './OutputSettings';

/**
 * Parameter names that are hidden from all sections (API keys sourced from
 * integrations, plus github_token which comes from integrations).
 */
const HIDDEN_PARAM_NAMES = new Set([
  'shodan_api_key',
  'dnsdb_api_key',
  'crunchbase_api_key',
  'opencorporates_api_key',
  'proxycurl_api_key',
  'diffbot_api_key',
  'securitytrails_api_key',
  'virustotal_api_key',
  'binaryedge_api_key',
  'apollo_api_key',
  'censys_api_key',
  'censys_org_id',
  'viewdns_api_key',
  'github_token',
]);

/** Maps plugin names to their configurable parameters and mode. */
const PLUGIN_PARAMS: Record<
  string,
  { mode: 'active' | 'passive'; label: string; params: string[] }
> = {
  'doh-enum': {
    mode: 'active',
    label: 'DoH Enumeration',
    params: ['doh_servers', 'doh_gateways', 'doh_deploy_gateways'],
  },
  'dns-brute': {
    mode: 'active',
    label: 'DNS Brute Force',
    params: ['dns_brute_concurrency'],
  },
  'google-dorks': {
    mode: 'passive',
    label: 'Google Dorks',
    params: ['google_dorks_max_subsidiaries'],
  },
};

/** All parameter names that belong to a plugin section. */
const ALL_PLUGIN_PARAM_NAMES = new Set(
  Object.values(PLUGIN_PARAMS).flatMap(p => p.params)
);

function isPluginSectionVisible(
  pluginName: string,
  pluginMode: 'active' | 'passive',
  currentMode: string,
  selectedPlugins: string[]
): boolean {
  // If specific plugins are selected, only show sections for those plugins
  if (selectedPlugins.length > 0) {
    return selectedPlugins.includes(pluginName);
  }
  // No plugins selected = all applicable plugins run based on mode
  if (currentMode === 'all') return true;
  if (currentMode === 'active') return pluginMode === 'active';
  if (currentMode === 'passive') return pluginMode === 'passive';
  // Default: show if mode matches or mode is empty (show all)
  return currentMode === '' || pluginMode === currentMode;
}

function CollapsiblePluginSection({
  pluginName,
  label,
  params,
  parameterValues,
  onParameterChange,
  readOnly,
  onParameterBlur,
}: {
  pluginName: string;
  label: string;
  params: CapabilityParameter[];
  parameterValues: Record<string, string>;
  onParameterChange: (paramName: string, value: string) => void;
  readOnly: boolean;
  onParameterBlur?: (paramName: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border-chariot-stroke rounded border">
      <button
        type="button"
        id={`${pluginName}-button`}
        aria-expanded={isOpen}
        aria-controls={`${pluginName}-panel`}
        onClick={() => setIsOpen(!isOpen)}
        className="hover:bg-chariot-background-secondary flex w-full items-center gap-2 px-3 py-2 text-left transition-colors"
      >
        <ChevronRightIcon
          className={twMerge(
            'size-4 shrink-0 transition-transform duration-200',
            isOpen && 'rotate-90'
          )}
        />
        <span className="text-chariot-text-primary text-sm font-medium">
          {label}
        </span>
        <span className="text-chariot-text-secondary text-xs">
          ({pluginName})
        </span>
      </button>
      <div
        id={`${pluginName}-panel`}
        role="region"
        aria-labelledby={`${pluginName}-button`}
        className={twMerge(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-in-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3 px-3 pt-1 pb-3">
            {params.map((param, index) => (
              <ParameterInput
                key={`plugin-${pluginName}-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => onParameterChange(param.name, value)}
                onBlur={onParameterBlur}
                readOnly={readOnly}
                isRequired={false}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Maps parameter names to the attack_plugins values that require them.
 * Parameters listed here are only visible when at least one of their
 * associated plugins is selected in the attack_plugins parameter.
 */
const PARAMETER_PLUGIN_DEPS: Record<string, string[]> = {
  c2_repo: ['github/interactive-shell', 'github/runner-on-runner'],
  target_os: ['github/runner-on-runner'],
  target_arch: ['github/runner-on-runner'],
  runner_labels: ['github/runner-on-runner'],
  delivery: ['github/ai-prompt-injection'],
  persistence_method: ['github/persistence'],
};

function isParameterVisible(
  paramName: string,
  parameterValues: Record<string, string>
): boolean {
  const deps = PARAMETER_PLUGIN_DEPS[paramName];
  if (!deps) return true; // No dependency — always visible

  const selectedPlugins = parameterValues['attack_plugins'];
  if (!selectedPlugins) return false; // Dependency exists but no plugins selected

  const selected = new Set(
    selectedPlugins
      .split(',')
      .map(s => s.trim())
      .filter(Boolean)
  );
  return deps.some(plugin => selected.has(plugin));
}

interface ParametersSectionProps {
  capability: Capability;
  parameterValues: Record<string, string>;
  setParameterValues: Dispatch<SetStateAction<Record<string, string>>>;
  validationErrors: Record<string, boolean>;
  setValidationErrors: Dispatch<SetStateAction<Record<string, boolean>>>;
  isPraetorianUser: boolean;
  largeArtifact: boolean;
  setLargeArtifact: (value: boolean) => void;
  onParameterBlur?: (paramName: string) => void;
}

/**
 * ParametersSection - Component for displaying and editing capability parameters
 *
 * Displays configurable parameters for a capability, with different views
 * for Praetorian users (editable) and customers (read-only).
 */
export function ParametersSection({
  capability,
  parameterValues,
  setParameterValues,
  validationErrors,
  setValidationErrors,
  isPraetorianUser,
  largeArtifact,
  setLargeArtifact,
  onParameterBlur,
}: ParametersSectionProps) {
  // Handler for parameter value changes
  const handleParameterChange = (paramName: string, value: string) => {
    setParameterValues(prev => ({
      ...prev,
      [paramName]: value,
    }));
    // Clear validation error when user starts typing
    if (validationErrors[paramName]) {
      setValidationErrors(prev => ({
        ...prev,
        [paramName]: false,
      }));
    }
  };

  if (!capability.parameters || capability.parameters.length === 0) {
    return (
      <div className="h-fit">
        {/* Output Settings - Only show for capabilities that support big output */}
        {isPraetorianUser && capability.largeArtifact && (
          <OutputSettings
            largeArtifact={largeArtifact}
            setLargeArtifact={setLargeArtifact}
          />
        )}
        <div className="py-8 text-center">
          <p className="text-chariot-text-secondary">
            This capability has no configurable parameters.
          </p>
        </div>
      </div>
    );
  }

  // Derive visibility context from current parameter values
  const currentMode = parameterValues['mode'] ?? '';
  const selectedPlugins = parameterValues['plugins']
    ? parameterValues['plugins'].split(',').filter(Boolean)
    : [];

  // Required parameters, filtering by plugin visibility
  const visibleParams = (capability.parameters || []).filter(param =>
    isParameterVisible(param.name, parameterValues)
  );
  const requiredParams = visibleParams.filter(param => param.required === true);

  // General optional params: not required, not hidden, not plugin-specific
  const generalOptionalParams = visibleParams.filter(
    param =>
      (param.required === false || param.required === undefined) &&
      !HIDDEN_PARAM_NAMES.has(param.name) &&
      !ALL_PLUGIN_PARAM_NAMES.has(param.name)
  );

  // Build visible plugin sections
  const visiblePluginSections = Object.entries(PLUGIN_PARAMS)
    .filter(([pluginName, config]) =>
      isPluginSectionVisible(
        pluginName,
        config.mode,
        currentMode,
        selectedPlugins
      )
    )
    .map(([pluginName, config]) => ({
      pluginName,
      label: config.label,
      params: config.params
        .map(paramName =>
          capability.parameters!.find(p => p.name === paramName)
        )
        .filter((p): p is CapabilityParameter => p !== undefined),
    }))
    .filter(section => section.params.length > 0);

  return (
    <div className="h-fit">
      {/* Required Parameters */}
      {requiredParams.length > 0 && (
        <div className="mb-8">
          <div className="flex flex-col gap-3">
            {requiredParams.map((param, index) => (
              <ParameterInput
                key={`required-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                onBlur={onParameterBlur}
                error={
                  validationErrors[param.name]
                    ? `${param.name} is required`
                    : undefined
                }
                readOnly={!isPraetorianUser}
                isRequired={true}
              />
            ))}
          </div>
        </div>
      )}

      {/* Output Settings - Only show for capabilities that support big output */}
      {isPraetorianUser && capability.largeArtifact && (
        <OutputSettings
          largeArtifact={largeArtifact}
          setLargeArtifact={setLargeArtifact}
        />
      )}

      {/* General Optional Parameters */}
      {generalOptionalParams.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md text-chariot-text-primary mb-4 font-medium">
            Optional Parameters
          </h4>
          <div className="flex flex-col gap-3">
            {generalOptionalParams.map((param, index) => (
              <ParameterInput
                key={`optional-${index}`}
                param={param}
                value={parameterValues[param.name] || ''}
                onChange={value => handleParameterChange(param.name, value)}
                readOnly={!isPraetorianUser}
                isRequired={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Plugin Configuration Sections */}
      {visiblePluginSections.length > 0 && (
        <div>
          <h4 className="text-md text-chariot-text-primary mb-4 font-medium">
            Plugin Configuration
          </h4>
          <div className="flex flex-col gap-2">
            {visiblePluginSections.map(section => (
              <CollapsiblePluginSection
                key={section.pluginName}
                pluginName={section.pluginName}
                label={section.label}
                params={section.params}
                parameterValues={parameterValues}
                onParameterChange={handleParameterChange}
                readOnly={!isPraetorianUser}
                onParameterBlur={onParameterBlur}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
